import React, { useEffect, useState, useMemo } from 'react';
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  Box,
  Tooltip,
  FormControlLabel,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  IconButton,
  Collapse
} from '@mui/material';
import { MdPerson } from 'react-icons/md';
import axiosInstance from '../../utils/axiosConfig';

import { 
  Diversity3, 
  TheaterComedy, // Stage Manager
  Headphones, // Sound Engineer
  Business, // Production Manager
  DirectionsRun, // Runner
  Store, // Front of House
  People, // Staff
  Construction, // Stage Hand
  RecordVoiceOver, // Artist Liaison
  CameraAlt, // Photographer/Videographer
  VideoSettings, // Vision Mix Engineer
  VolunteerActivism as VolunteerIcon, // Volunteer
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

interface Staff {
  _id: string;
  name: string;
  roles: string[];
  profileImage: string;
}

interface AssignedStaff {
  staffId: string;
  assignedRole?: string;
}

interface Performance {
  _id: string;
  staff: AssignedStaff[]; // Assigned staff with optional roles
}

interface StaffRosterProps {
  performanceId: string;
  onAssignmentComplete?: (updatedPerformance: any) => void;
}

const roleColors: Record<string, string> = {
 "Stage Manager": "#2D4A6B",        // Dark muted blue
 "Sound Engineer": "#4A3B66",       // Dark muted purple  
 "Production Manager": "#6B3B3B",   // Dark muted red
 "Runner": "#3B5C4A",               // Dark muted green
 "Front of House": "#6B5A2D",       // Dark muted gold
 "Staff": "#4A5C3B",                // Dark muted olive
 "Stage Hand": "#2D4A66",           // Dark steel blue
 "Artist Liaison": "#6B2D4A",       // Dark muted magenta
 "Photographer/Videographer": "#5C4A2D", // Dark muted brown
 "Vision Mix Engineer": "#5A2D6B",  // Dark muted violet
 "Volunteer": "#2D5C5A",            // Dark muted teal
 "Lighting Engineer": "#4A2D6B",    // Dark muted indigo
 "Structural Engineer": "#4A4A5C",  // Dark muted slate
 "Backline Supplier": "#5C3B2D",    // Dark muted rust
 "Supplier": "#5C2D3B"              // Dark muted burgundy
};

const roleIcons: Record<string, React.ReactElement> = {
  "Stage Manager": <TheaterComedy fontSize="small" />,
  "Sound Engineer": <Headphones fontSize="small" />,
  "Production Manager": <Business fontSize="small" />,
  "Runner": <DirectionsRun fontSize="small" />,
  "Front of House": <Store fontSize="small" />,
  "Staff": <People fontSize="small" />,
  "Stage Hand": <Construction fontSize="small" />,
  "Artist Liaison": <RecordVoiceOver fontSize="small" />,
  "Photographer/Videographer": <CameraAlt fontSize="small" />,
  "Vision Mix Engineer": <VideoSettings fontSize="small" />,
  "Volunteer": <VolunteerIcon fontSize="small" />
};

const StaffRoster: React.FC<StaffRosterProps> = ({ performanceId, onAssignmentComplete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<AssignedStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedStaff, setExpandedStaff] = useState<string | null>(null);

  const handleListItemClick = (staffId: string, event: React.MouseEvent) => {
    // Don't toggle selection if clicking the Select component
    if ((event.target as HTMLElement).closest('.MuiSelect-root')) {
      return;
    }
    handleToggle(staffId);
  };

  useEffect(() => {
    fetchData();
  }, [performanceId]);

  const fetchData = async () => {
    try {
      const [staffRes, perfRes] = await Promise.all([
        axiosInstance.get('/api/personnel'),
        axiosInstance.get(`/api/performances/${performanceId}`)
      ]);
  
      console.log('Fetched Performance:', perfRes.data);
      console.log('Pre-assigned Staff:', perfRes.data.staff);
  
      setStaff(staffRes.data);
  
      // Transform the existing staff assignments to match our expected format
      const existingAssignments = perfRes.data.staff?.map((staffMember: any) => ({
        staffId: typeof staffMember.staffId === 'object' ? staffMember.staffId._id : staffMember.staffId,
        assignedRole: staffMember.assignedRole || ""
      })) || [];
  
      console.log('Transformed existing assignments:', existingAssignments);
      setSelectedStaff(existingAssignments);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
      setLoading(false);
    }
  };

  const handleToggle = (staffId: string) => {
    setSelectedStaff(prev => {
      const exists = prev.some(s => s.staffId === staffId);
      if (exists) {
        return prev.filter(s => s.staffId !== staffId); // Uncheck staff
      } else {
        return [...prev, { staffId, assignedRole: "" }]; // Ensure empty role, so UI updates correctly
      }
    });
  };

  const handleRoleChange = (staffId: string, assignedRole: string) => {
    setSelectedStaff(prev =>
      prev.map(s => 
        s.staffId === staffId ? { ...s, assignedRole } : s
      )
    );
  };

  const handleSelectAll = () => {
    setSelectedStaff(prev =>
      prev.length === staff.length ? [] : staff.map(s => ({ staffId: s._id, assignedRole: "" }))
    );
  };

  const handleAssignStaff = async () => {
    try {
      const response = await axiosInstance.put(
        `/api/performances/${performanceId}/assign-staff`,
        { staffAssignments: selectedStaff }
      );
  
      if (onAssignmentComplete) {
        onAssignmentComplete(response.data);
      }
      setSelectedStaff(response.data.staff);
    } catch (err) {
      console.error('Error assigning staff:', err);
      setError('Failed to assign staff members. Please try again.');
    }
  };

  const allSelected = useMemo(() => staff.length > 0 && selectedStaff.length === staff.length, [staff, selectedStaff]);

  if (loading) return <Box className="flex justify-center items-center p-4"><CircularProgress /></Box>;
  if (error) return <Typography color="error" className="p-4">{error}</Typography>;
  if (staff.length === 0) return <Typography className="p-4">No staff members available.</Typography>;

  // Desktop version (original)
  if (!isMobile) {
    return (
      <div className="p-4">
        <Typography variant="h4" gutterBottom color="white" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Diversity3 fontSize="small" />
          Assign Staff
        </Typography>

        {/* Select All Checkbox */}
        <FormControlLabel
          control={<Checkbox checked={allSelected} onChange={handleSelectAll} />}
          label="Select All"
          className="mb-2"
        />

        <List className="mb-4">
          {staff.map((member) => {
            const selectedEntry = selectedStaff.find(s => s.staffId === member._id);
            const isSelected = !!selectedEntry;
            const assignedRole = selectedEntry?.assignedRole || "";
            
            return (
              <ListItem
                key={member._id}
                dense
                button
                onClick={(e) => handleListItemClick(member._id, e)}
                className={`transition-all p-3 rounded-md cursor-pointer ${
                  isSelected ? 'bg-gray-200 border-l-4 border-green-500' : 'hover:bg-gray-50'
                }`}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(member._id);
                    }}
                    sx={{
                      transform: "scale(1.5)",  // Bigger checkbox
                      color: isSelected ? "#4CAF50" : undefined, // Green if selected
                    }}
                  />
                </ListItemIcon>

                <Avatar
                  key={member.profileImage ? member.profileImage : 'default'} 
                  className="mr-3"
                  src={member.profileImage && member.profileImage.trim() !== "" ? member.profileImage : undefined}
                >
                  {!member.profileImage || member.profileImage.trim() === "" ? <MdPerson /> : null}
                </Avatar>
                
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={isSelected ? "bold" : "normal"}
                      sx={{ color: isSelected ? "#eef520" : undefined }}
                    >
                      {member.name}
                    </Typography>
                  }
                  secondary={
                    <Box className="flex flex-wrap gap-2 mt-1">
                      {member.roles.map((role, index) => (
                        <Chip
                          key={index}
                          label={role}
                          size="small"
                          icon={roleIcons[role]} 
                          style={{ backgroundColor: roleColors[role] || "#ccc", color: "#fff" }}
                        />
                      ))}
                    </Box>
                  }
                />

                {/* Role dropdown */}
                <Select
                  value={assignedRole}
                  onChange={(e) => handleRoleChange(member._id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  displayEmpty
                  size="small"
                  className="MuiSelect-root"
                  sx={{ minWidth: 160, ml: 2 }}
                >
                  <MenuItem value="">No Specific Role</MenuItem>
                  {member.roles.map((role, index) => (
                    <MenuItem 
                      key={index} 
                      value={role}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {roleIcons[role]}
                        {role}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </ListItem>
            );
          })}
        </List>

        {/* Assign Button */}
        <Tooltip title={selectedStaff.length === 0 ? "Select at least one staff member" : ""}>
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAssignStaff}
              disabled={selectedStaff.length === 0}
              fullWidth
            >
              Assign Selected Staff
            </Button>
          </span>
        </Tooltip>
      </div>
    );
  }

  // Mobile version (same visual style, simplified content)
  return (
    <div className="p-4">
      <Typography variant="h4" gutterBottom color="white" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Diversity3 fontSize="small" />
        Assign Staff
      </Typography>

      {/* Select All Checkbox */}
      <FormControlLabel
        control={<Checkbox checked={allSelected} onChange={handleSelectAll} />}
        label="Select All"
        className="mb-2"
      />

      <List className="mb-4">
        {staff.map((member) => {
          const selectedEntry = selectedStaff.find(s => s.staffId === member._id);
          const isSelected = !!selectedEntry;
          const assignedRole = selectedEntry?.assignedRole || "";
          
          return (
            <ListItem
              key={member._id}
              dense
              button
              onClick={(e) => handleListItemClick(member._id, e)}
              className={`transition-all p-3 rounded-md cursor-pointer ${
                isSelected ? 'bg-gray-200 border-l-4 border-green-500' : 'hover:bg-gray-50'
              }`}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(member._id);
                  }}
                  sx={{
                    transform: "scale(1.5)",
                    color: isSelected ? "#4CAF50" : undefined,
                  }}
                />
              </ListItemIcon>

              <Avatar
                key={member.profileImage ? member.profileImage : 'default'} 
                className="mr-3"
                src={member.profileImage && member.profileImage.trim() !== "" ? member.profileImage : undefined}
              >
                {!member.profileImage || member.profileImage.trim() === "" ? <MdPerson /> : null}
              </Avatar>
              
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    fontWeight={isSelected ? "bold" : "normal"}
                    sx={{ color: isSelected ? "#eef520" : undefined }}
                  >
                    {member.name}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {member.roles.length} role{member.roles.length !== 1 ? 's' : ''} available
                  </Typography>
                }
              />

              {/* Role dropdown */}
              <Select
                value={assignedRole}
                onChange={(e) => handleRoleChange(member._id, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                displayEmpty
                size="small"
                className="MuiSelect-root"
                sx={{ minWidth: 140, ml: 2 }}
              >
                <MenuItem value="">No Specific Role</MenuItem>
                {member.roles.map((role, index) => (
                  <MenuItem 
                    key={index} 
                    value={role}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </ListItem>
          );
        })}
      </List>

      {/* Assign Button */}
      <Tooltip title={selectedStaff.length === 0 ? "Select at least one staff member" : ""}>
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAssignStaff}
            disabled={selectedStaff.length === 0}
            fullWidth
          >
            Assign Selected Staff
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};

export default StaffRoster;